import { createBrowserRouter , Link, Navigate, Outlet, RouterProvider, useLocation, useNavigate} from 'react-router'

import IconButton from '@mui/material/IconButton';
import { AppBar, Box, Button, CssBaseline, FormControl, InputLabel, Menu, MenuItem, Select, Toolbar, Typography, useMediaQuery, useTheme } from "@mui/material";
import AccountTreeRoundedIcon from '@mui/icons-material/AccountTreeRounded';
import MenuBookRoundedIcon from '@mui/icons-material/MenuBookRounded';
import { Global } from "@emotion/react";
import { useQueryClient } from '@tanstack/react-query';

import { MainSelector } from "./components/MainSelector.tsx";
import { RadialAscendency } from './tab/RadialAscendency.tsx'
import { AncestryMap } from "./tab/AncestryMap.tsx";
import { Occupations } from "./tab/Occupations.tsx";
import { NotFound } from "./NotFound.tsx"
import { Index } from "./Index.tsx";
import { LandingPage } from "./LandingPage.tsx";
import { OidcCompletePage } from "./OidcCompletePage.tsx";
import { PasswordPage } from "./account/PasswordPage.tsx";
import { useEffect, useState, useSyncExternalStore } from 'react';
import { MobileCompatDrawer } from './components/EventDrawer.tsx';
import { StoriesIndex, StoryPage } from './stories/StoryPages.tsx';
import { ChatAssistant } from './components/ChatAssistant.tsx';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import { NotableEvents } from './tab/NotableEvents.tsx';
import { clearBearerToken, ensureGrampsToken, fetchCurrentUser, readBearerToken, startGrampsTokenAutoRefresh, subscribeToBearerToken } from './grampsAuth.ts';
import { getActiveGrampsProfileId, listGrampsProfiles, readCurrentUserUuid, setActiveGrampsProfile } from './grampsAuth.ts';
import { captureSpaPageView, captureSpaTabView, identifyAnalyticsUser, resetAnalyticsUser } from './analytics.ts';
import { getViewPath, getViewRouteFromPathname } from './viewRoute.ts';
import { getDefaultTreeName, rememberLastLoadedTreeName } from './treeName.ts';
import { setUnauthorizedHandler } from './api/grampsApi.ts';
import { DEFAULT_SITE_TITLE, updateTitleFromCurrentTree } from './siteTitle.ts';
import { useTransactionPolling } from './hooks/useTransactionPolling.ts';

import * as QRCode from 'qrcode';

const DEFAULT_LEFT_PANE_WIDTH = 900;
// Same ceiling the resize handle enforces, so the default cannot swallow the whole
// window on a narrow desktop. Kept in sync with MAX_LEFT_PANE_WIDTH_RATIO in EventDrawer.
const MAX_LEFT_PANE_WIDTH_VW = 60;
const DEFAULT_LEFT_PANE_CSS = `min(${DEFAULT_LEFT_PANE_WIDTH}px, ${MAX_LEFT_PANE_WIDTH_VW}vw)`;

function getTabNameFromPathname(pathname: string): string | null {
  const tab = getViewRouteFromPathname(pathname).tab;
  if (tab) return tab;
  if (pathname.startsWith('/view')) return 'view';
  return null;
}

function AppShell({ onAuthRequired }: { onAuthRequired?: () => void }) {
  const location = useLocation();
  const route = getViewRouteFromPathname(location.pathname);
  const defaultTreeName = getDefaultTreeName();

  useEffect(() => {
    void updateTitleFromCurrentTree();

    // On a cold load several components 401 at once (index notes, map, ascendency, ...),
    // sometimes for different tree profiles at once. Share one refresh attempt per
    // failed token instead of racing independent ones for the same profile, since a
    // late resolution from a request that started before login would otherwise reopen the
    // dialog right after the user just logged in through an earlier 401's retry.
    const pendingTokenRefreshes = new Map<string, Promise<string | null>>();

    setUnauthorizedHandler((failedToken) => {
      // The access token lives 15 minutes but the refresh token doesn't expire,
      // so a 401 (e.g. after reopening the browser) usually just means the access
      // token went stale, not that the user is really logged out. Try a silent
      // refresh before bothering them with the login dialog. The refresh targets
      // the profile the failed token belongs to (not just the "active" one) so a
      // healthy active session can't mask a dead one for a different tree.
      const key = failedToken ?? '';
      void (async () => {
        let pending = pendingTokenRefreshes.get(key);
        if (!pending) {
          pending = ensureGrampsToken(failedToken).finally(() => {
            pendingTokenRefreshes.delete(key);
          });
          pendingTokenRefreshes.set(key, pending);
        }
        const refreshedToken = await pending;
        if (!refreshedToken) {
          onAuthRequired?.();
        }
      })();
    });

    const stop = startGrampsTokenAutoRefresh(() => {
      onAuthRequired?.();
    }, 4 * 60 * 1000);

    return () => {
      setUnauthorizedHandler(null);
      stop();
      document.title = DEFAULT_SITE_TITLE;
    };
  }, [onAuthRequired]);

  if (!route.treeName && (location.pathname === '/view' || location.pathname.startsWith('/view/'))) {
    const targetPath = `/${encodeURIComponent(defaultTreeName)}${location.pathname}`;

    return <Navigate to={{ pathname: targetPath, search: location.search, hash: location.hash }} replace />;
  }

  return <AppBarLayout onOpenAuthDialog={onAuthRequired ?? (() => {})} />;
}

export function App({ onAuthRequired }: { onAuthRequired?: () => void }) {
    const router = createBrowserRouter([
      {
        path: '/',
        Component: LandingPage,
      },
      {
        path: '/oidc/complete',
        Component: OidcCompletePage,
      },
      {
        // Where an invitation or a forgotten password's mail leads: the
        // service's own accounts, outside the Gramps sign-in of the shell.
        path: '/compte/mot-de-passe',
        Component: PasswordPage,
      },
      {
        Component: () => <AppShell onAuthRequired={onAuthRequired} />,
        children: [
          {
            path: ':treeName',
            children: [
              {
                index: true,
                Component: Index,
              },
              {
                path: 'view',
                Component: MainSelector,
              },
              {
                // The tree's chronicles and portraits, published from the
                // atelier: a page of its own, beside the tree's index.
                path: 'recits',
                Component: StoriesIndex,
              },
              {
                path: 'recits/:storyId',
                Component: StoryPage,
              },
              {
                // The drawer of a person the story names, open over the story.
                path: 'recits/:storyId/person/:sideItem',
                Component: StoryPage,
              },
              {
                // A person's drawer before any main person is chosen, with the
                // selector to pick one.
                path: 'person/:sideItem',
                Component: MainSelector,
              },
              {
                path: 'view/:main',
                Component: MainSelector,
                children: [
                  { index: true, Component: RadialAscendency },
                  { path: 'ascendency/:sideItem?', Component: RadialAscendency },
                  { path: 'maps/:sideItem?', Component: AncestryMap },
                  { path: 'occupations/:sideItem?', Component: Occupations },
                  { path: 'notableevents/:sideItem?', Component: NotableEvents },
                ],
              },
            ],
          },
          {
            path: '*',
            Component: NotFound,
          },
        ],
      },
    ]);

    return (
        <Box sx={{ flexGrow: 1 }}>
          <CssBaseline />
          <Global
            styles={{
              ':root': {
                '--event-drawer-default-width': `${DEFAULT_LEFT_PANE_WIDTH}px`,
              },
              '.MuiDrawer-root.MuiDrawer-docked > .MuiPaper-root': {
                height: "100%",
                overflow: 'scroll',
              },
            }}
          />
          <RouterProvider router={router} />
        </Box>
    )
}

function AppBarLayout({ onOpenAuthDialog }: { onOpenAuthDialog: () => void }) {
  const location = useLocation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const legacySearchParams = new URLSearchParams(location.search)
  const viewRoute = getViewRouteFromPathname(location.pathname)
  const fullUrl = `${window.location.origin}${location.pathname}${location.search}${location.hash}`
  const selectedId = viewRoute.sideItemId ?? viewRoute.mainId ?? legacySearchParams.get("sideItem")
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // < 600px [web:1]
  const id = viewRoute.mainId ?? legacySearchParams.get("main")
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null)
  const [currentUser, setCurrentUser] = useState<string | null>(null)
  const [profiles, setProfiles] = useState(() => listGrampsProfiles())
  const [activeProfileId, setActiveProfileId] = useState<string | null>(() => getActiveGrampsProfileId())
  const homeTreeName = viewRoute.treeName ?? getDefaultTreeName()
  const homePath = `/${encodeURIComponent(homeTreeName)}`
  // The two sections of the site, for the tree the page is on (/:tree/…).
  const pageTree = viewRoute.treeName
    ?? (location.pathname.split('/').filter(Boolean).map(decodeURIComponent)[0] || homeTreeName)
  // The tree opens on the last main person seen, not on the selector again.
  const [lastMainId, setLastMainId] = useState<string | null>(id)
  if (id && id !== lastMainId) setLastMainId(id)
  const treePath = getViewPath('ascendency', lastMainId, undefined, pageTree)
  const storiesPath = `/${encodeURIComponent(pageTree)}/recits`
  const onStories = location.pathname.startsWith(storiesPath)
  const onTree = !onStories && (location.pathname.includes('/view') || location.pathname.includes('/person/'))

  useTransactionPolling(viewRoute.treeName)
  // Reactive so a token obtained after a 401 (silent refresh or the login dialog)
  // re-fires the effect below -- it's a one-shot imperative fetch, not react-query-
  // managed, so it wouldn't otherwise pick up on the app-wide `invalidateQueries()`
  // that runs on login, and the account menu would keep showing no user until reload.
  const bearerToken = useSyncExternalStore(subscribeToBearerToken, () => readBearerToken())

  useEffect(() => {
    if (viewRoute.treeName) {
      rememberLastLoadedTreeName(viewRoute.treeName)
    }
  }, [viewRoute.treeName])

  useEffect(() => {
    void fetchCurrentUser().then((name) => {
      setCurrentUser(name)

      const userId = readCurrentUserUuid()
      if (!userId) {
        return
      }

      const profileId = getActiveGrampsProfileId()
      const properties: Record<string, string> = {}
      if (name) {
        properties.display_name = name
      }
      if (profileId) {
        properties.gramps_profile_id = profileId
      }

      identifyAnalyticsUser(userId, properties)
    })
  }, [bearerToken])

  useEffect(() => {
    QRCode.toDataURL(fullUrl).then(url => {
      setQrCodeUrl(url)
    })
  }, [fullUrl])

  useEffect(() => {
    captureSpaPageView(location.pathname, '', location.hash)
  }, [location.pathname, location.hash])

  useEffect(() => {
    const tab = getTabNameFromPathname(location.pathname)
    if (!tab) {
      return
    }

    const props: Record<string, string> = {
      pathname: location.pathname,
    }
    if (id) {
      props.main_id = id
    }
    if (selectedId) {
      props.side_item_id = selectedId
    }
    if (activeProfileId) {
      props.gramps_profile_id = activeProfileId
    }

    captureSpaTabView(tab, props)
  }, [location.pathname, id, selectedId, activeProfileId])

  // Suspending the laptop freezes JS timers, so any fetch in flight at that moment
  // (or started right as it wakes, before the network is actually back up) exhausts
  // its retries against a still-dead connection and is left stuck in error state --
  // the browser's 'online' event is unreliable across suspend/resume and can't be
  // relied on to trigger react-query's own refetchOnReconnect. Retrying only the
  // queries that are actually in error keeps this cheap: it doesn't re-run the
  // CPU-heavy ancestor queries that already succeeded and are just sitting stale,
  // unlike a blanket invalidateQueries().
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') {
        return
      }
      void queryClient.refetchQueries({ predicate: (query) => query.state.status === 'error' })
    }

    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [queryClient])

  const logout = () => {
    resetAnalyticsUser();
    clearBearerToken();
    setAnchorEl(null);
    window.location.reload();
  }

  const handleSwitchProfile = (profileId: string) => {
    if (!profileId) {
      return
    }
    const selectedProfile = profiles.find((profile) => profile.id === profileId)
    const switched = setActiveGrampsProfile(profileId)
    if (!switched) {
      return
    }
    setActiveProfileId(profileId)
    setAnchorEl(null)

    const nextTreeName = String(selectedProfile?.label || selectedProfile?.username || '').trim().toLowerCase()
    if (!nextTreeName) {
      return
    }

    navigate({
      pathname: `/${encodeURIComponent(nextTreeName)}`,
      search: location.search,
      hash: location.hash,
    }, { replace: true })
  }

  const handleOpenAccountMenu = (event: React.MouseEvent<HTMLElement>) => {
    setProfiles(listGrampsProfiles())
    setActiveProfileId(getActiveGrampsProfileId())
    setAnchorEl(event.currentTarget)
  }

  return <Box sx={{ flexGrow: 1 }}>
    <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
      <Toolbar>
        <IconButton
          size='large'
          edge="start"
          color="inherit"
          aria-label="menu"
          sx={{ mr: 2 }}
          component={Link} to={homePath}
        >
          <img src="/favicon.svg" alt="accueil" width={36} height={36} style={{ display: 'block' }} />
        </IconButton>
        {!isMobile && (
          <Typography variant="h6" component="div" sx={{ mr: 3 }}>
            Généalogie
          </Typography>
        )}
        <Box component="nav" sx={{ display: 'flex', gap: { xs: 0.5, sm: 1 }, flexGrow: 1 }}>
          {[
            { label: 'Arbre', to: treePath, active: onTree, icon: <AccountTreeRoundedIcon /> },
            { label: 'Récits', to: storiesPath, active: onStories, icon: <MenuBookRoundedIcon /> },
          ].map((section) => (
            <Button
              key={section.label}
              component={Link}
              to={section.to}
              color="inherit"
              startIcon={section.icon}
              aria-current={section.active ? 'page' : undefined}
              sx={{
                textTransform: 'none',
                fontSize: '1rem',
                px: 1.5,
                borderRadius: 2,
                backgroundColor: section.active ? 'rgba(255, 255, 255, 0.18)' : 'transparent',
                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.25)' },
              }}
            >
              {section.label}
            </Button>
          ))}
        </Box>
        <IconButton component="a" size="large" color="inherit" aria-label="account" sx={{ mr: 2 }} onClick={handleOpenAccountMenu}>
          <AccountCircleIcon sx={{ mr: 1 }} />
        </IconButton>
        <Menu
          id="menu-appbar"
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          keepMounted
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
        >
          {profiles.length > 0 && (
            <MenuItem disableRipple disableTouchRipple sx={{ py: 1.5 }}>
              <FormControl fullWidth size="small">
                <InputLabel id="tree-profile-select-label">Arbre / compte</InputLabel>
                <Select
                  labelId="tree-profile-select-label"
                  value={activeProfileId || ''}
                  label="Arbre / compte"
                  onChange={(event) => handleSwitchProfile(String(event.target.value || ''))}
                >
                  {profiles.map((profile) => (
                    <MenuItem key={profile.id} value={profile.id}>
                      {profile.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </MenuItem>
          )}
          <MenuItem
            onClick={() => {
              setAnchorEl(null)
              onOpenAuthDialog()
            }}
            title='Ajouter un compte'
          >
            Ajouter un compte / arbre
          </MenuItem>
          {currentUser && (
            <MenuItem disabled sx={{ opacity: '1 !important', fontStyle: 'italic', color: 'text.secondary', fontSize: '0.85rem' }}>
              Connecté en tant que&nbsp;<strong>{currentUser}</strong>
            </MenuItem>
          )}
          <MenuItem onClick={() => setAnchorEl(null)} title='Contact'>
            Contactez moi à&nbsp;<a href="mailto:jerome.viveret@gmail.com">jerome.viveret@gmail.com</a> !
          </MenuItem>
          <MenuItem>
            {qrCodeUrl ? <img src={qrCodeUrl} alt="QR Code" /> : null}
          </MenuItem>
          <MenuItem onClick={logout} title='Se déconnecter'>
            <LogoutIcon fontSize="small" sx={{ mr: 1 }} />
            Se déconnecter
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
    <Box sx={{ marginTop: "50px", height: "calc(100vh - 50px)", overflowX: 'hidden' }}>
      {selectedId && <MobileCompatDrawer></MobileCompatDrawer>}
      <Box component="main" sx={{
        p: 3,
        boxSizing: 'border-box',
        minWidth: 0,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflowX: 'hidden',
        // Over a story the drawer covers the margin, not the text: no shift.
        marginLeft: selectedId && !isMobile && !viewRoute.storyId ? `var(--event-drawer-width, ${DEFAULT_LEFT_PANE_CSS})` : "0",
        width: selectedId && !isMobile && !viewRoute.storyId ? `calc(100% - var(--event-drawer-width, ${DEFAULT_LEFT_PANE_CSS}))` : "100%",
        transition: 'margin-left 0.05s linear, width 0.05s linear'
      }}>
        <Outlet/>
      </Box>
    </Box>
    <ChatAssistant treeName={homeTreeName} />
  </Box>
}
